import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { notify } from "utils/notify";
import {
  AuthContainer,
  AuthSidebar,
  LogoContainer,
  LogoSeparator,
  BrandLogo,
  AnniversaryLogo,
  AuthTitle,
  AuthContent,
  PoolProLogo,
  SidebarFormContainer,
  SidebarFormTitle,
  AuthDescription,
  AuthMessageBlock,
  FormGroup,
  Label,
  PasswordField,
  PasswordInput,
  PasswordToggle,
  ErrorMessage,
  Button,
  AuthLink,
  LinkRow,
} from "styles/auth.styles";
import daveyLogo from "assets/Davey_logotype_Yellow.svg";
import anniversaryLogo from "assets/90_years_logo_White.svg";
import poolProfessionalLogo from "assets/PoolProfessional_STACK_Yellow_White.png";
import { resetPassword } from "services/authService";

type ResetPasswordFormData = {
  password: string;
  confirmPassword: string;
};

const schema = yup.object().shape({
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
    ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Please confirm your password"),
});

const ResetPasswordConfirmation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetCompleted, setResetCompleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const pin = searchParams.get("token") || "";
  const hasMissingData = !pin;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (hasMissingData) {
      setErrorMessage("Invalid reset link. Missing required information.");
    }
  }, [hasMissingData]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (hasMissingData) return;
    setIsSubmitting(true);
    try {
      await resetPassword({ token: pin, newPassword: data.password });
      setResetCompleted(true);
      notify.success("Your password has been reset successfully!");
      setTimeout(() => navigate("/auth/signin"), 3000);
    } catch (error: any) {
      console.error("Password reset error:", error);
      const message =
        error.response?.data?.message ||
        "Failed to reset password. Please try again.";
      notify.error(message);
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (resetCompleted) {
      return (
        <AuthMessageBlock>
          <SidebarFormTitle>Password Reset Complete</SidebarFormTitle>
          <AuthDescription>
            Your password has been reset successfully. You will be redirected to
            the login page shortly.
          </AuthDescription>
          <LinkRow>
            <AuthLink as={Link} to="/auth/signin">
              Go to Login
            </AuthLink>
          </LinkRow>
        </AuthMessageBlock>
      );
    }

    if (errorMessage && hasMissingData) {
      return (
        <AuthMessageBlock>
          <SidebarFormTitle>Invalid Reset Link</SidebarFormTitle>
          <AuthDescription>{errorMessage}</AuthDescription>
          <LinkRow>
            <AuthLink as={Link} to="/auth/forgot-password">
              Request a new reset link
            </AuthLink>
          </LinkRow>
        </AuthMessageBlock>
      );
    }

    return (
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <SidebarFormTitle>Reset Your Password</SidebarFormTitle>
        <AuthDescription>Please enter your new password below.</AuthDescription>

        <FormGroup>
          <Label htmlFor="password">New Password</Label>
          <PasswordField>
            <PasswordInput
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your new password"
              autoComplete="new-password"
              {...register("password")}
              disabled={isSubmitting}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              disabled={isSubmitting}
            >
              <i className={`pi ${showPassword ? "pi-eye-slash" : "pi-eye"}`} />
            </PasswordToggle>
          </PasswordField>
          {errors.password && (
            <ErrorMessage>{errors.password.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <PasswordField>
            <PasswordInput
              id="confirmPassword"
              type={showConfirm ? "text" : "password"}
              placeholder="Confirm your new password"
              autoComplete="new-password"
              {...register("confirmPassword")}
              disabled={isSubmitting}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              aria-label={
                showConfirm ? "Hide confirm password" : "Show confirm password"
              }
              disabled={isSubmitting}
            >
              <i className={`pi ${showConfirm ? "pi-eye-slash" : "pi-eye"}`} />
            </PasswordToggle>
          </PasswordField>
          {errors.confirmPassword && (
            <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>
          )}
        </FormGroup>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Resetting..." : "Reset Password"}
        </Button>

        <LinkRow>
          <AuthLink as={Link} to="/auth/signin">
            Back to Login
          </AuthLink>
        </LinkRow>
      </form>
    );
  };

  return (
    <AuthContainer>
      <AuthSidebar>
        <LogoContainer>
          <BrandLogo src={daveyLogo} alt="Davey Logo" />
          <LogoSeparator />
          <AnniversaryLogo src={anniversaryLogo} alt="90 Years Logo" />
        </LogoContainer>
        <AuthTitle>PoolProfessional</AuthTitle>
        <SidebarFormContainer>{renderContent()}</SidebarFormContainer>
      </AuthSidebar>
      <AuthContent>
        <PoolProLogo
          src={poolProfessionalLogo}
          alt="Davey Pool Professional Logo"
        />
      </AuthContent>
    </AuthContainer>
  );
};

export default ResetPasswordConfirmation;
